import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus, Mail, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, Card, goldBtn } from '../ui';
import { api, eur, errMsg, fmtDateTime } from '../api';
import { usePortal } from '../PortalAuth';
import { PERMISSION_SECTIONS } from '../shells/nav';
import MemberForm from '../team/MemberForm';
import { ActivityLog, TopupRequests } from '../team/TeamParts';

const label = (id) => PERMISSION_SECTIONS.find((s) => s.id === id)?.label || id;

function MemberRow({ m, onEdit, onChanged }) {
  const resend = async () => { try { await api.post(`/team/${m.id}/invite`); toast.success('Invito inviato di nuovo'); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { if (!window.confirm(`Eliminare l'accesso di ${m.email}?`)) return; await api.delete(`/team/${m.id}`); toast.success('Utente eliminato'); onChanged(); };
  const p = m.permissions || {};
  return (
    <div className="py-4 border-t border-[#1F242C] flex flex-wrap items-start gap-4" data-testid={`team-member-${m.id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm text-[#F2F2F2]">{m.name} <span className="text-[#8A8F97]">· {m.email}</span></p>
        <p className="text-[11px] text-[#5A5F67] mt-0.5">{m.invited ? 'Invito in attesa' : m.last_portal_login ? `Ultimo accesso ${fmtDateTime(m.last_portal_login)}` : 'Mai entrato'}{!m.active && ' · accesso sospeso'}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(p.sections || []).map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 border border-[#1F242C] rounded-sm text-[#C9CCD1]">{label(s)}</span>)}
          {!(p.sections || []).length && <span className="text-[10px] text-[#8A8F97]">Solo Panoramica</span>}
        </div>
        <p className="text-[11px] text-[#8A8F97] mt-2">{p.purchase ? 'Acquisti consentiti' : 'Nessun acquisto'} · {p.topup ? 'può chiedere ricariche' : 'niente ricariche'} · {m.cap_cents != null ? `tetto ${eur(m.cap_cents)}/mese, speso ${eur(m.spent_month_cents)}` : `nessun tetto, speso ${eur(m.spent_month_cents)} questo mese`}</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} className="h-8 px-3 text-xs border border-[#1F242C] rounded-sm inline-flex items-center gap-1.5 hover:border-[#D4AF37]" data-testid={`team-edit-${m.id}`}><Pencil size={13} />Permessi</button>
        {m.invited && <button onClick={resend} className="h-8 px-3 text-xs border border-[#1F242C] rounded-sm inline-flex items-center gap-1.5" data-testid={`team-resend-${m.id}`}><Mail size={13} />Reinvia</button>}
        <button onClick={remove} className="h-8 px-2 text-xs border border-[#1F242C] rounded-sm text-[#E37A72]" aria-label="Elimina" data-testid={`team-delete-${m.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { user } = usePortal();
  const [members, setMembers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get('/team').then((r) => setMembers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="team-page">
      <PageHeader kicker={user.company_name || 'Account aziendale'} title="Utenti e permessi">
        <button onClick={() => setEdit('new')} className={`${goldBtn} h-10 px-4 text-sm inline-flex items-center gap-2`} data-testid="team-invite-btn"><UserPlus size={15} />Invita utente</button>
      </PageHeader>
      <p className="text-sm text-[#8A8F97] -mt-4 mb-6 max-w-3xl">Sei il titolare dell'account: solo tu crei gli utenti e decidi cosa vedono. Tutti usano lo stesso plafond e la stessa fatturazione; chi ha accesso a una sezione vede i documenti dell'azienda in quella sezione.</p>
      <TopupRequests />
      <Card className="p-6 mb-6" data-testid="team-list">
        {!members.length && <p className="text-sm text-[#5A5F67]" data-testid="team-empty">Nessun utente invitato. Invita i colleghi e assegna a ciascuno le sezioni di sua competenza.</p>}
        {members.map((m) => <MemberRow key={m.id} m={m} onEdit={() => setEdit(m)} onChanged={load} />)}
      </Card>
      <ActivityLog members={members} />
      {edit && <MemberForm member={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
